import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useSelector } from "react-redux";

import api from "../../api";
import CollapsiblePanel from "../../components/redesign/modules/CollapsiblePanel";
import { PrimaryButton, SecondaryButton } from "../../components/redesign/elements/Button";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { UserRoleAssignment, AgencyRoleOption } from "../../types/userAccessTypes";
import { errorToaster, successToaster } from "../../helpers/toasters";

const PanelWrap = styled("div")({
  marginBottom: 24,
});

const Description = styled("p")(({ theme }: { theme: Theme }) => ({
  margin: "0 0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray500,
}));

const AddRow = styled("form")({
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 16,
});

const AgencyPicker = styled("div")({
  position: "relative" as const,
  minWidth: 280,
  maxWidth: 420,
  flex: "1 1 280px",
});

const AgencySearch = styled("input")(({ theme }: { theme: Theme }) => ({
  width: "100%",
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  boxSizing: "border-box" as const,
  fontFamily: "inherit",
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
  "&::placeholder": { color: theme.palette.brand.gray500 },
}));

const AgencyMenu = styled("div")(({ theme }: { theme: Theme }) => ({
  marginTop: 4,
  maxHeight: 240,
  overflowY: "auto" as const,
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(15, 43, 76, 0.12)",
}));

const AgencyOption = styled("button")(({ theme }: { theme: Theme }) => ({
  display: "block",
  width: "100%",
  border: "none",
  background: "transparent",
  textAlign: "left" as const,
  padding: "8px 12px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  cursor: "pointer",
  fontFamily: "inherit",
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
  '&[data-selected="true"]': {
    background: theme.palette.brand.gray100,
    fontWeight: 600,
  },
}));

const AgencyOptionMeta = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "block",
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray500,
}));

const RoleSelect = styled("select")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  fontFamily: "inherit",
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
}));

const List = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  maxHeight: 320,
  overflowY: "auto" as const,
});

const Row = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "8px 12px",
  borderRadius: 8,
  background: theme.palette.brand.gray50,
}));

const Username = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const AgencyHint = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray500,
  marginLeft: 8,
}));

const RoleLabel = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.gray500,
  marginLeft: 8,
  textTransform: "capitalize",
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
}));

const AdminRolesPanel: FC = () => {
  const currentUser = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const currentUsername = String(currentUser).trim().toLowerCase();
  const [assignments, setAssignments] = useState<UserRoleAssignment[]>([]);
  const [agencies, setAgencies] = useState<AgencyRoleOption[]>([]);
  const [agencyId, setAgencyId] = useState("");
  const [agencyQuery, setAgencyQuery] = useState("");
  const [isAgencyMenuOpen, setIsAgencyMenuOpen] = useState(false);
  const [role, setRole] = useState<"admin" | "superadmin">("admin");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const agencyPickerRef = useRef<HTMLDivElement>(null);

  const superadminCount = useMemo(
    () => assignments.filter((assignment) => assignment.role === "superadmin").length,
    [assignments],
  );

  const agenciesByCreator = useMemo(() => {
    const grouped: Record<string, string[]> = {};
    agencies.forEach((agency) => {
      if (!grouped[agency.createdBy]) grouped[agency.createdBy] = [];
      grouped[agency.createdBy].push(agency.name);
    });
    return grouped;
  }, [agencies]);

  const selectedAgency = useMemo(() => agencies.find((agency) => agency.agencyId === agencyId), [agencies, agencyId]);

  const filteredAgencies = useMemo(() => {
    const query = agencyQuery.trim().toLowerCase();
    if (!query) return agencies;
    return agencies.filter((agency) => `${agency.name} ${agency.createdBy}`.toLowerCase().includes(query));
  }, [agencies, agencyQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (agencyPickerRef.current && !agencyPickerRef.current.contains(event.target as Node)) {
        setIsAgencyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadPanelData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [rolesResponse, agenciesResponse] = await Promise.all([
        api.fetchUserRoleAssignments(),
        api.fetchAgenciesForRoleGrant(),
      ]);
      setAssignments(rolesResponse.success && Array.isArray(rolesResponse.payload) ? rolesResponse.payload : []);
      setAgencies(agenciesResponse.success && Array.isArray(agenciesResponse.payload) ? agenciesResponse.payload : []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("load admin roles panel error:", error);
      setAssignments([]);
      setAgencies([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPanelData();
  }, [loadPanelData]);

  const handleGrant = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextUsername = selectedAgency?.createdBy.trim();
    if (!nextUsername) return;

    if (nextUsername === currentUsername) {
      errorToaster("You cannot change your own role");
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.assignUserRole(nextUsername, role);
      if (response.success) {
        successToaster(
          `${role === "superadmin" ? "Superadmin" : "Admin"} role granted to ${nextUsername} (${selectedAgency?.name})`,
        );
        setAgencyId("");
        setAgencyQuery("");
        await loadPanelData();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async (assignmentUsername: string) => {
    if (assignmentUsername === currentUsername) {
      errorToaster("You cannot revoke your own admin role");
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.revokeUserRole(assignmentUsername);
      if (response.success) {
        successToaster(`Admin role removed from ${assignmentUsername}`);
        await loadPanelData();
      } else if (response.error) {
        errorToaster(response.error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PanelWrap>
      <CollapsiblePanel title="Admin users" defaultExpanded={false}>
        <Description>
          Select an agency to grant a role to its creator (Cognito username in createdBy). Only superadmins can manage
          this list.
        </Description>
        <AddRow onSubmit={handleGrant}>
          <AgencyPicker ref={agencyPickerRef}>
            <AgencySearch
              type="text"
              placeholder="Search agencies…"
              value={isAgencyMenuOpen ? agencyQuery : selectedAgency ? `${selectedAgency.name} (${selectedAgency.createdBy})` : ""}
              onChange={(event) => {
                setAgencyQuery(event.target.value);
                if (agencyId) setAgencyId("");
              }}
              onFocus={() => {
                setIsAgencyMenuOpen(true);
                setAgencyQuery("");
              }}
              aria-label="Agency"
            />
            {isAgencyMenuOpen ? (
              <AgencyMenu>
                {filteredAgencies.length ? (
                  filteredAgencies.map((agency) => (
                    <AgencyOption
                      key={agency.agencyId}
                      type="button"
                      data-selected={agency.agencyId === agencyId ? "true" : "false"}
                      onClick={() => {
                        setAgencyId(agency.agencyId);
                        setAgencyQuery("");
                        setIsAgencyMenuOpen(false);
                      }}
                    >
                      {agency.name}
                      <AgencyOptionMeta>{agency.createdBy}</AgencyOptionMeta>
                    </AgencyOption>
                  ))
                ) : (
                  <EmptyState>No agencies match this search.</EmptyState>
                )}
              </AgencyMenu>
            ) : null}
          </AgencyPicker>
          <RoleSelect
            value={role}
            onChange={(event) => setRole(event.target.value as "admin" | "superadmin")}
            aria-label="Role to grant"
          >
            <option value="admin">Admin</option>
            <option value="superadmin">Superadmin</option>
          </RoleSelect>
          <PrimaryButton type="submit" disabled={isSaving || !selectedAgency}>
            Grant role
          </PrimaryButton>
        </AddRow>
        {isLoading ? (
          <EmptyState>Loading roles…</EmptyState>
        ) : assignments.length ? (
          <List>
            {assignments.map((assignment) => {
              const isSelf = assignment.username === currentUsername;
              const isLastSuperadmin = assignment.role === "superadmin" && superadminCount <= 1;
              const agencyNames = agenciesByCreator[assignment.username];
              return (
                <Row key={assignment.username}>
                  <div>
                    <Username>{assignment.username}</Username>
                    <RoleLabel>{assignment.role}</RoleLabel>
                    {agencyNames?.length ? <AgencyHint>{agencyNames.join(", ")}</AgencyHint> : null}
                  </div>
                  <SecondaryButton
                    type="button"
                    disabled={isSaving || assignments.length === 1 || isSelf || isLastSuperadmin}
                    onClick={() => handleRevoke(assignment.username)}
                  >
                    Revoke
                  </SecondaryButton>
                </Row>
              );
            })}
          </List>
        ) : (
          <EmptyState>No admin users assigned yet.</EmptyState>
        )}
      </CollapsiblePanel>
    </PanelWrap>
  );
};

export default AdminRolesPanel;
